import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ArrowLeftRight, Receipt } from "lucide-react";

const TYPES = ["", "ENTRADA", "RETIRADA", "DEVOLUCAO", "AJUSTE", "INVENTARIO"];
const TAG = {
  ENTRADA: "bg-emerald-950/60 text-emerald-300", RETIRADA: "bg-rose-950/60 text-rose-300",
  DEVOLUCAO: "bg-sky-950/60 text-sky-300", AJUSTE: "bg-amber-950/60 text-amber-300", INVENTARIO: "bg-purple-950/60 text-purple-300",
};

export default function Movements() {
  const [movs, setMovs] = useState(null);
  const [tipo, setTipo] = useState("");
  const { user } = useAuth();
  const canEnter = ["ADMINISTRADOR", "ALMOXARIFADO"].includes(user?.role);

  useEffect(() => {
    setMovs(null);
    api.get("/movements", { params: { tipo: tipo || undefined, limit: 300 } }).then((r) => setMovs(r.data));
  }, [tipo]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="font-display text-3xl font-bold text-slate-50">Movimentações</h1><p className="text-slate-400 text-sm">Trilha de auditoria de todas as movimentações de estoque.</p></div>
        {canEnter && (
          <Button asChild className="bg-sky-700 hover:bg-sky-600" data-testid="movements-nfe-entry">
            <Link to="/entrada-nfe"><Receipt className="h-4 w-4 mr-2" />Entrada por NF-e</Link>
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {TYPES.map((t) => (
          <Button key={t || "all"} data-testid={`filter-${t || "todos"}`} size="sm" variant={tipo === t ? "default" : "secondary"} onClick={() => setTipo(t)} className={tipo === t ? "bg-sky-700" : ""}>{t || "Todos"}</Button>
        ))}
      </div>

      {movs === null ? <Skeleton className="h-96 bg-slate-800" /> : (
        <Card className="card-metal p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-950 text-slate-400 text-xs uppercase font-mono">
                <th className="text-left px-4 py-3">Data/Hora</th><th className="text-left px-4 py-3">Peça</th><th className="text-left px-4 py-3">Tipo</th>
                <th className="text-right px-4 py-3">Qtd</th><th className="text-right px-4 py-3">Estoque</th><th className="text-left px-4 py-3">Máquina</th>
                <th className="text-left px-4 py-3">Retirado por</th><th className="text-left px-4 py-3">Registrado por</th><th className="text-left px-4 py-3">Motivo</th>
              </tr></thead>
              <tbody>
                {movs.map((mv) => (
                  <tr key={mv.id} data-testid={`movement-row-${mv.id}`} className="border-t border-slate-800 hover:bg-slate-900/40">
                    <td className="px-4 py-2.5 text-slate-400 whitespace-nowrap">{fmtDateTime(mv.data)}</td>
                    <td className="px-4 py-2.5 text-slate-200">{mv.part_desc}</td>
                    <td className="px-4 py-2.5"><span className={`rounded px-2 py-0.5 text-xs font-medium ${TAG[mv.tipo] || "bg-slate-800"}`}>{mv.tipo}</span></td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-200">{mv.quantidade}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-500">{mv.estoque_anterior ?? "—"}→{mv.estoque_novo ?? "—"}</td>
                    <td className="px-4 py-2.5 text-slate-300">{mv.machine_nome || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-300">{(mv.tipo === "RETIRADA" || mv.tipo === "DEVOLUCAO") ? (mv.retirado_por_nome || "—") : "—"}</td>
                    <td className="px-4 py-2.5 text-slate-400">{mv.usuario_nome}</td>
                    <td className="px-4 py-2.5 text-slate-500">{mv.motivo || "—"}</td>
                  </tr>
                ))}
                {movs.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-500"><ArrowLeftRight className="h-8 w-8 mx-auto mb-2 opacity-50" />Nenhuma movimentação.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
